import type { FC, PropsWithChildren, ReactElement } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import {
    AVATAR_SIZE_BY_CONTENT_SIZE_MAP,
    BADGE_SIZE_BY_CONTENT_SIZE_MAP,
    ICON_SIZE_BY_CONTENT_SIZE_MAP,
    LINK_SIZE_BY_CONTENT_SIZE_MAP,
    PICTURE_SIZE_BY_CONTENT_SIZE_MAP,
    TAG_SIZE_BY_CONTENT_SIZE_MAP,
} from "./constants/sizes";
import { CONTENT_DISPLAY_NAME } from "./utils/core";
import { createTestIdForContentElement } from "./utils/getTestId";
import { Gap } from "../../../helpers/common/Gap/Gap";
import { getChildrenRecord } from "../../../utils/react";

import type {
    ContentAlignmentX,
    ContentAlignmentY,
    ContentSize,
    ContentView,
} from "./types";
import type { PropsWithTsId } from "../../../types/react";

import classes from "./Content.styl";

export const CONTENT_TEST_ID = createTestIdForContentElement("root");

export const CONTENT_DEFAULT_SIZE = "15";
export const CONTENT_DEFAULT_VIEW = "primary";

export interface IContent {
    /** Отвечает за значение параметра `size` у всех компонентов внутри `Content` */
    size?: ContentSize;
    /** Отвечает за комбинацию цветовых токенов, присвоенных всех компонентам внутри `Content` */
    view?: ContentView;
    /** Отвечает за выравнивание компонентов по горизонтали */
    alignmentX?: ContentAlignmentX;
    /** Отвечает за выравнивание по вертикали Content.Text и Content.Dot */
    textAlignmentY?: ContentAlignmentY;
    /** Отвечает за выравнивание всех компонентов по вертикали, кроме Content.Text и Content.Dot */
    addonsAlignmentY?: ContentAlignmentY;
}

export type ContentProps = PropsWithChildren<IContent> & Partial<PropsWithTsId>;

/**
 * Компонент, который может отобразить набор контента в виде Avatar, Picture, Icon, Tag, Link, внутренних компонентов Text и Dot, а также любого другого контента.
 * Отображает компоненты в том порядке, в котором их передали. Но в спеках они будут в определенном порядке и ограниченном количестве.
 *
 * Служит для формирования строк контента в более сложных компонентах и ячейках
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/💠-Base-Components?node-id=2-106033&t=i9KoZkQn1PFd13wW-1" target="_blank">Спека</a>
 * @constructor
 */
export const Content: FC<ContentProps> = ({
    children,
    size = CONTENT_DEFAULT_SIZE,
    view = CONTENT_DEFAULT_VIEW,
    alignmentX = "start",
    textAlignmentY = "center",
    addonsAlignmentY = "center",
    "data-tsid": tsid,
}): ReactElement => {
    const avatarSize =
        AVATAR_SIZE_BY_CONTENT_SIZE_MAP[
            size as keyof typeof AVATAR_SIZE_BY_CONTENT_SIZE_MAP
        ];

    const pictureSize =
        PICTURE_SIZE_BY_CONTENT_SIZE_MAP[
            size as keyof typeof PICTURE_SIZE_BY_CONTENT_SIZE_MAP
        ];

    const linkSize =
        LINK_SIZE_BY_CONTENT_SIZE_MAP[
            size as keyof typeof LINK_SIZE_BY_CONTENT_SIZE_MAP
        ];

    const tagSize =
        TAG_SIZE_BY_CONTENT_SIZE_MAP[
            size as keyof typeof TAG_SIZE_BY_CONTENT_SIZE_MAP
        ];

    const expandedChildren = useMemo(() => {
        return getChildrenRecord(
            children,
            "Content",
            {
                Avatar: {
                    props: {
                        size: avatarSize,
                    },

                    skipElement: !avatarSize,
                },

                Badge: {
                    props: {
                        size: BADGE_SIZE_BY_CONTENT_SIZE_MAP[size],
                    },
                },

                Tag: {
                    props: {
                        size: tagSize,
                    },

                    skipElement: !tagSize,
                },

                Icon: {
                    props: {
                        size: ICON_SIZE_BY_CONTENT_SIZE_MAP[size],
                        view,
                    },
                },

                Picture: {
                    props: {
                        size: pictureSize,
                    },

                    skipElement: !pictureSize,
                },

                Link: {
                    props: {
                        size: linkSize,
                    },

                    skipElement: !linkSize,
                },

                Text: {
                    props: {
                        size,
                        view,
                        selfAlignmentY: textAlignmentY,
                    },
                },

                Dot: {
                    props: {
                        size,
                        view,
                        selfAlignmentY: textAlignmentY,
                        className: classes["dot"],
                    },
                },
            },
            { isArrayResult: true },
        );
    }, [
        avatarSize,
        children,
        linkSize,
        pictureSize,
        size,
        tagSize,
        view,
        textAlignmentY,
    ]);

    const contentClassName = cn(
        classes["content"],
        classes[`__alignment-x-${alignmentX}`],
        classes[`__alignment-y-${addonsAlignmentY}`],
    );

    return (
        <Gap
            className={classes["wrapper"]}
            contentClassName={contentClassName}
            data-tsid={tsid || CONTENT_TEST_ID}
            data-uikit={CONTENT_DISPLAY_NAME}
        >
            {expandedChildren}
        </Gap>
    );
};

Content.displayName = CONTENT_DISPLAY_NAME;

export default Content;
